import { Injectable, inject } from '@angular/core';
import type { AbstractControl, ValidationErrors } from '@angular/forms';
import type { BankingIbanValidatorConfig } from '@domains/validators/banking-iban-validator-config';
import { environment } from '@environment';
import { CustomEventsEnum } from '@models/custom-events';
import { Store } from '@ngrx/store';
import { UpdateBankingBankInfos } from '@stores/banking/banking-ngrx.action';
import { userId } from '@wizbii-utils/angular/stores';
import { BankingUserWebservice } from '@wizbii-utils/angular/webservices';
import type { Observable } from 'rxjs';
import { catchError, map, of, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class BankingIbanValidator implements BankingIbanValidatorConfig {
  #bankingUserWebservice = inject(BankingUserWebservice);
  readonly #store = inject(Store);
  readonly #userId = this.#store.selectSignal(userId);

  readonly country = environment.i18n.country;
  readonly regexByCountry = {
    FR: /^[F|f][R|r]\d{2}\d{5}\d{5}[\dA-Z]{11}\d{2}$/,
    IT: /^[I|i][T|t]\d{2}[A-Z]{1}\d{5}\d{5}[\dA-Z]{12}$/,
    ES: /^[E|e][S|s]\d{2}\d{4}\d{4}\d{2}\d{10}$/,
  };
  readonly #IBAN_REGEXP = this.regexByCountry[this.country];

  validate(control: AbstractControl): Observable<ValidationErrors | null> {
    const ibanCleaned: string = control.value?.toUpperCase()?.trim()?.replace(/\s/g, '');
    if (!ibanCleaned.match(this.#IBAN_REGEXP)) {
      return this.setInvalidIban('pattern');
    }

    if (this.#userId()) {
      return this.#bankingUserWebservice.verifiyIbanMatchPartner(this.#userId()!, ibanCleaned).pipe(
        tap((bankInfos) => {
          window.dispatchEvent(new CustomEvent(CustomEventsEnum.ValidIbanInsertedEvent));

          if (bankInfos.iban) {
            this.#store.dispatch(UpdateBankingBankInfos({ bankInfos }));
          }
        }),
        map((bankInfos) => (!bankInfos.iban ? { invalidIban: true } : null)),
        catchError((err) => {
          return this.setInvalidIban(err?.error?.type);
        })
      );
    }

    return this.setInvalidIban();
  }

  setInvalidIban = (validationErrorType?: string): Observable<{ [key: string]: boolean }> => {
    this.#store.dispatch(UpdateBankingBankInfos({ bankInfos: null }));
    return validationErrorType ? of({ [validationErrorType]: true }) : of({ invalidIban: true });
  };
}
